import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Layout from '../common/Layout';
import DataDelayIndicator from '../common/DataDelayIndicator';
import ColumnSelector from './ColumnSelector';
import BeginnerQuestionsAccordion from './BeginnerQuestionsAccordion';
import BeginnerFlowWizard, { WizardFilters } from './beginner-flow/BeginnerFlowWizard';
import WizardPromptBanner from './WizardPromptBanner';
import ScreenerHeader from './ScreenerHeader';
import BeginnerProgressBar from '../common/BeginnerProgressBar';
import StockCard from './StockCard';
import ScreenerTable from './ScreenerTable';
import { SparklesIcon } from '@heroicons/react/24/outline';
import SelectedStockDisplay from '../common/SelectedStockDisplay';
import ActiveFilterPills from './ActiveFilterPills';
import { AdvancedFilters, FilterSidebarWrapper, COVERED_CALL_DEFAULT_FILTERS } from './filters';
import { LIQUIDITY_RULE, OI_SLIDER_MAX, useLiquidityRule } from '../../utils/liquidityRule';
import { COLUMN_DEFINITIONS, StockScreenerData } from './columnDefinitions';
import { apiService } from '../../services/api';
import { useBeginnerProgress } from '../../contexts/BeginnerProgressContext';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { usePresets } from '../../hooks/usePresets';
import { useFramework } from '../../contexts/FrameworkContext';
import { useStrategy } from '../../contexts/StrategyContext';
import StrategyToggle from '../common/StrategyToggle';
import { useLatestPrices } from '../../hooks/useLatestPrices';
import SavePresetModal from '../presets/SavePresetModal';
import { Preset } from '../../types/presets';
import ScoreChip from '../common/ScoreChip';
import Spinner from '../common/Spinner';

const StockScreener: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setStep, markStepComplete } = useBeginnerProgress();
  const { activeScoringFramework, scoringFrameworks, setActiveScoringFramework, refreshFrameworks } = useFramework();
  const { strategy } = useStrategy();

  // Presets - fetch once at parent level, pass to children
  // Note: systemPresets is always empty now (4 legacy presets were dropped —
  // see migration a7c91f4b5d2e). Stock profiles replaced them.
  const { systemPresets, isLoading: presetsLoading } = usePresets();

  // Data state
  const [displayedStocks, setDisplayedStocks] = useState<StockScreenerData[]>([]);
  const [totalStocks, setTotalStocks] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true); // Start true - load data on mount
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chart data cache for batch loading (beginner mode only)
  const [chartDataCache, setChartDataCache] = useState<Record<string, { results: any[] }>>({});
  
  // Progressive enrichment state
  const [enrichmentProgress, setEnrichmentProgress] = useState({ enriched: 0, total: 0, percentage: 0 });
  
  // Dataset-level timestamp
  const [lastDataUpdate, setLastDataUpdate] = useState<string | null>(null);
  
  // UI state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    COLUMN_DEFINITIONS.filter(col => col.isDefault).map(col => col.id)
  );
  // "Score" = the total score (the backend maps cc_score -> csp_score on the CSP
  // lens), so the cards read in score order. The old tiered cc_rank default
  // (Timing bucket, then Income fit) ranked 81 above 88 under a "Score" label.
  const [sortBy, setSortBy] = useState<string>('score');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Cards | Table view (desktop/tablet only; phones always get cards). Remembered per
  // browser as a convenience; storage can be unavailable, so every access is guarded.
  const [viewModePref, setViewModePref] = useState<'cards' | 'table'>(() => {
    try {
      return localStorage.getItem('screenerViewMode') === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const [isWideScreen, setIsWideScreen] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => setIsWideScreen(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const viewMode: 'cards' | 'table' = isWideScreen ? viewModePref : 'cards';
  // Filter sidebar can be hidden on desktop to give the results the full width (remembered per browser).
  const [filtersHidden, setFiltersHidden] = useState<boolean>(() => {
    try { return localStorage.getItem('screenerFiltersHidden') === '1'; } catch { return false; }
  });
  const toggleFilters = () => {
    setFiltersHidden((h) => {
      try { localStorage.setItem('screenerFiltersHidden', h ? '0' : '1'); } catch { /* in-memory only */ }
      return !h;
    });
  };
  const changeViewMode = (mode: 'cards' | 'table') => {
    setViewModePref(mode);
    try {
      localStorage.setItem('screenerViewMode', mode);
    } catch {
      /* storage unavailable: keep the in-memory choice */
    }
  };
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  // Pagination state - Start with 30 items for faster loading (beginner-friendly)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  
  // Filtering state
  const [searchInput, setSearchInput] = useState(''); // User typing input (for autocomplete)
  const [searchTerm, setSearchTerm] = useState(''); // Confirmed search filter (applied to results)
  const [filterCriteria, setFilterCriteria] = useState<{
    minCCScore: number;
    maxCCScore: number;
    minAnnualizedYield: number;
    maxRSI: number;
    minIVRank: number;
    maxIVRank: number;
    marketCapFilter: string;
    sectorFilter: string;
    maxSpreadPct: number | undefined;
  }>({
    minCCScore: 0,
    maxCCScore: 100,
    minAnnualizedYield: 0,
    maxRSI: 100,
    minIVRank: 0,
    maxIVRank: 100,
    marketCapFilter: 'all', // 'all', 'mega', 'large', 'mid', 'small'
    sectorFilter: 'all',
    maxSpreadPct: LIQUIDITY_RULE.maxSpreadPct // Liquidity rule default (user-adjustable)
  });

  // Beta: Force beginner mode for all users
  const [isBeginnerMode] = useState(true);
  // Initialize with covered call defaults (API-compatible format)
  const [beginnerFilters, setBeginnerFilters] = useState<any>({
    min_iv_rank: COVERED_CALL_DEFAULT_FILTERS.iv_rank_min,
    min_rsi: COVERED_CALL_DEFAULT_FILTERS.rsi_min,
    max_rsi: COVERED_CALL_DEFAULT_FILTERS.rsi_max,
    market_cap_categories: COVERED_CALL_DEFAULT_FILTERS.market_cap_categories?.join(','),
    exclude_earnings_within_days: COVERED_CALL_DEFAULT_FILTERS.exclude_earnings_within_days,
    min_dimension1_stars: COVERED_CALL_DEFAULT_FILTERS.min_dimension1_stars,
    min_dimension2_stars: COVERED_CALL_DEFAULT_FILTERS.min_dimension2_stars,
    min_open_interest: COVERED_CALL_DEFAULT_FILTERS.open_interest_min,
  });
  const [beginnerFilterDetails, setBeginnerFilterDetails] = useState<{
    investmentAmount: string;
    objective: string;
    companyQuality: string;
    activeFilters: any;
  } | null>(null);
  // Wizard completion state - default to true so stocks show immediately
  const [wizardCompleted, setWizardCompleted] = useState(true);
  // Wizard modal visibility (opens as overlay when user wants to use wizard)
  const [showWizardModal, setShowWizardModal] = useState(false);
  // Wizard prompt banner visibility (disabled for now - will decide later what to do with wizard)
  const [showWizardBanner, setShowWizardBanner] = useState(false);
  // Default expanded on desktop (≥1024px), collapsed on tablet/mobile.
  // Respect user's persisted preference if set.
  const [showFiltersPanel, setShowFiltersPanel] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem('screener.filtersPanel');
      if (stored === 'open') return true;
      if (stored === 'closed') return false;
    } catch {
      /* localStorage unavailable */
    }
    return window.innerWidth >= 1024;
  });

  // Persist toggle so power users don't fight the default each visit
  useEffect(() => {
    try {
      window.localStorage.setItem('screener.filtersPanel', showFiltersPanel ? 'open' : 'closed');
    } catch {
      /* ignore */
    }
  }, [showFiltersPanel]);

  // Store original filter values for toggle functionality
  // Initialize with covered call defaults
  const [originalFilters, setOriginalFilters] = useState<{
    ownedStocks?: string[];
    min_net_margin?: number;
    max_debt_equity?: number;
    min_iv_rank?: number;
    min_rsi?: number;
    max_rsi?: number;
    trend_filter?: string;
    max_spread_pct?: number;
  }>({
    min_iv_rank: COVERED_CALL_DEFAULT_FILTERS.iv_rank_min,
    min_rsi: COVERED_CALL_DEFAULT_FILTERS.rsi_min,
    max_rsi: COVERED_CALL_DEFAULT_FILTERS.rsi_max,
    max_spread_pct: COVERED_CALL_DEFAULT_FILTERS.spread_pct_max,
  });
  
  // Advanced search state (matching stock analysis)
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<{symbol: string; name: string}[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSortingRef = useRef<boolean>(false);
  
  // Enhanced request caching to prevent duplicate API calls (including StrictMode duplicates)
  const lastRequestRef = useRef<string>('');
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeRequestRef = useRef<Promise<void> | null>(null);
  const strictModeBlockerRef = useRef<boolean>(false);
  // Track if filter effect is initial render (to skip - mount effect handles initial load)
  const isFilterEffectInitialRender = useRef<boolean>(true);
  
  // Advanced Filter Modal state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({ ...COVERED_CALL_DEFAULT_FILTERS });

  // The page renders with today's liquidity rule; when the server rule arrives
  // (GET /api/stocks/liquidity-rule, utils/liquidityRule.ts) and differs, move the
  // defaults the user has not changed to it. Off on the server = no change here.
  const liquidityRuleVersion = useLiquidityRule();
  const lastLiquidityRuleRef = useRef({ spread: LIQUIDITY_RULE.maxSpreadPct, oi: LIQUIDITY_RULE.minOpenInterest });
  useEffect(() => {
    const prev = lastLiquidityRuleRef.current;
    const next = { spread: LIQUIDITY_RULE.maxSpreadPct, oi: LIQUIDITY_RULE.minOpenInterest };
    if (prev.spread === next.spread && prev.oi === next.oi) return;
    lastLiquidityRuleRef.current = next;
    setFilterCriteria(p => (p.maxSpreadPct === prev.spread ? { ...p, maxSpreadPct: next.spread } : p));
    setOriginalFilters(p => (p.max_spread_pct === prev.spread ? { ...p, max_spread_pct: next.spread } : p));
    setBeginnerFilters((p: any) => (p.min_open_interest === prev.oi ? { ...p, min_open_interest: next.oi } : p));
    setAdvancedFilters(p => {
      const moveSpread = p.spread_pct_max === prev.spread;
      const moveOi = p.open_interest_min === prev.oi;
      if (!moveSpread && !moveOi) return p;
      return {
        ...p,
        ...(moveSpread ? { spread_pct_max: next.spread } : {}),
        ...(moveOi ? { open_interest_min: next.oi } : {}),
      };
    });
  }, [liquidityRuleVersion]);

  // Preset state
  const [activePresetId, setActivePresetId] = useState<number | null>(null);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  // Track base preset when filters are modified
  const [basePresetId, setBasePresetId] = useState<number | null>(null);
  // Track whether filters have been modified from the base preset
  const [isPresetModified, setIsPresetModified] = useState(false);
  // Ref to track when we're applying a preset (skip modification detection)
  const isApplyingPresetRef = useRef<boolean>(false);

  // Handler for beginner filter changes
  const handleBeginnerFiltersChange = useCallback((filters: any, filterDetails?: {
    investmentAmount: string;
    objective: string;
    companyQuality: string;
    activeFilters: any;
  }) => {
    setBeginnerFilters(filters);
    setBeginnerFilterDetails(filterDetails || null);
  }, []);

  // Handler for wizard completion
  const handleWizardComplete = useCallback((filters: WizardFilters) => {
    console.log('Wizard completed with filters:', filters);

    // Convert wizard filters to screener format
    let screenFilters: any = {};

    // Path A: Owned stocks
    if (filters.ownedStocks && filters.ownedStocks.length > 0) {
      // Filter to show only owned stocks
      screenFilters.search = filters.ownedStocks.join(',');
    } else {
      // Path B: New shares - apply quality filters
      if (filters.min_net_margin !== undefined) {
        screenFilters.min_net_margin = filters.min_net_margin;
      }
      if (filters.max_debt_equity !== undefined) {
        screenFilters.max_debt_equity = filters.max_debt_equity;
      }
      if (filters.min_iv_rank !== undefined) {
        screenFilters.min_iv_rank = filters.min_iv_rank;
      }
      if (filters.min_rsi !== undefined) {
        screenFilters.min_rsi = filters.min_rsi;
      }
      if (filters.max_rsi !== undefined) {
        screenFilters.max_rsi = filters.max_rsi;
      }
      if (filters.trend_filter) {
        screenFilters.trend_filter = filters.trend_filter;
      }
    }

    // Store DTE and Delta ranges for contract filtering (used when navigating to analysis)
    const filterDetails = {
      investmentAmount: '',
      objective: filters.ownedStocks ? 'existing' : 'new',
      companyQuality: '',
      activeFilters: {
        ...screenFilters,
        ownedStocks: filters.ownedStocks, // Preserve owned stocks array for FilterSummary display
        minDays: filters.minDays,
        maxDays: filters.maxDays,
        minDelta: filters.minDelta,
        maxDelta: filters.maxDelta
      }
    };

    setBeginnerFilters(screenFilters);
    setBeginnerFilterDetails(filterDetails);
    setWizardCompleted(true);

    // Store original filter values for toggle functionality (stock filters only)
    setOriginalFilters({
      ownedStocks: filters.ownedStocks,
      min_net_margin: filters.min_net_margin,
      max_debt_equity: filters.max_debt_equity,
      min_iv_rank: filters.min_iv_rank,
      min_rsi: filters.min_rsi,
      max_rsi: filters.max_rsi,
      trend_filter: filters.trend_filter,
      max_spread_pct: LIQUIDITY_RULE.maxSpreadPct
    });

    // Update progress: wizard is complete (steps 1-3), now on step 4 (pick a stock)
    setStep(4);
  }, [setStep]);

  // Handler for banner dismiss (session-only)
  const handleBannerDismiss = useCallback(() => {
    setShowWizardBanner(false);
  }, []);

  // Handler for wizard completion from modal
  const handleWizardCompleteFromModal = useCallback((filters: WizardFilters) => {
    handleWizardComplete(filters);
    setShowWizardModal(false);
    setShowWizardBanner(false); // Hide banner after completing wizard
  }, [handleWizardComplete]);

  // Filter toggle handlers (for beginner mode filter pills)

  const handleToggleNetMarginFilter = useCallback(() => {
    setBeginnerFilters((prev: any) => {
      const isActive = prev.min_net_margin !== undefined && prev.min_net_margin >= 0;
      return {
        ...prev,
        min_net_margin: isActive ? undefined : originalFilters.min_net_margin
      };
    });
    setBeginnerFilterDetails(prev => {
      if (!prev) return prev;
      const isActive = prev.activeFilters.min_net_margin !== undefined;
      return {
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          min_net_margin: isActive ? undefined : originalFilters.min_net_margin
        }
      };
    });
  }, [originalFilters.min_net_margin]);

  const handleToggleDebtEquityFilter = useCallback(() => {
    setBeginnerFilters((prev: any) => {
      const isActive = prev.max_debt_equity !== undefined && prev.max_debt_equity >= 0;
      return {
        ...prev,
        max_debt_equity: isActive ? undefined : originalFilters.max_debt_equity
      };
    });
    setBeginnerFilterDetails(prev => {
      if (!prev) return prev;
      const isActive = prev.activeFilters.max_debt_equity !== undefined;
      return {
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          max_debt_equity: isActive ? undefined : originalFilters.max_debt_equity
        }
      };
    });
  }, [originalFilters.max_debt_equity]);

  const handleToggleIVRankFilter = useCallback(() => {
    setBeginnerFilters((prev: any) => {
      const isActive = prev.min_iv_rank !== undefined && prev.min_iv_rank >= 0;
      return {
        ...prev,
        min_iv_rank: isActive ? undefined : originalFilters.min_iv_rank
      };
    });
    setBeginnerFilterDetails(prev => {
      if (!prev) return prev;
      const isActive = prev.activeFilters.min_iv_rank !== undefined;
      return {
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          min_iv_rank: isActive ? undefined : originalFilters.min_iv_rank
        }
      };
    });
  }, [originalFilters.min_iv_rank]);

  const handleToggleRSIFilter = useCallback(() => {
    setBeginnerFilters((prev: any) => {
      const isActive = prev.min_rsi !== undefined && prev.max_rsi !== undefined;
      return {
        ...prev,
        min_rsi: isActive ? undefined : originalFilters.min_rsi,
        max_rsi: isActive ? undefined : originalFilters.max_rsi
      };
    });
    setBeginnerFilterDetails(prev => {
      if (!prev) return prev;
      const isActive = prev.activeFilters.min_rsi !== undefined;
      return {
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          min_rsi: isActive ? undefined : originalFilters.min_rsi,
          max_rsi: isActive ? undefined : originalFilters.max_rsi
        }
      };
    });
  }, [originalFilters.min_rsi, originalFilters.max_rsi]);

  const handleToggleTrendFilter = useCallback(() => {
    setBeginnerFilters((prev: any) => {
      const isActive = prev.trend_filter !== undefined && prev.trend_filter !== '';
      return {
        ...prev,
        trend_filter: isActive ? undefined : originalFilters.trend_filter
      };
    });
    setBeginnerFilterDetails(prev => {
      if (!prev) return prev;
      const isActive = prev.activeFilters.trend_filter !== undefined;
      return {
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          trend_filter: isActive ? undefined : originalFilters.trend_filter
        }
      };
    });
  }, [originalFilters.trend_filter]);

  const handleToggleOwnedStocksFilter = useCallback(() => {
    setBeginnerFilters((prev: any) => {
      const isActive = prev.search !== undefined && prev.search !== '';
      return {
        ...prev,
        search: isActive ? undefined : originalFilters.ownedStocks?.join(',')
      };
    });
    setBeginnerFilterDetails(prev => {
      if (!prev) return prev;
      const isActive = prev.activeFilters.ownedStocks !== undefined && prev.activeFilters.ownedStocks.length > 0;
      return {
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          ownedStocks: isActive ? [] : originalFilters.ownedStocks
        }
      };
    });
  }, [originalFilters.ownedStocks]);

  const handleToggleSpreadFilter = useCallback(() => {
    setFilterCriteria(prev => {
      const isActive = prev.maxSpreadPct !== undefined;
      return {
        ...prev,
        maxSpreadPct: isActive ? undefined : originalFilters.max_spread_pct
      };
    });
  }, [originalFilters.max_spread_pct]);

  // Handler for applying advanced filters from the modal
  const handleApplyAdvancedFilters = useCallback((filters: AdvancedFilters) => {
    setAdvancedFilters(filters);

    // Build new filters from scratch (don't merge with old beginnerFilters to allow proper reset)
    const mergedFilters: any = {};

    // IV Rank
    if (filters.iv_rank_min !== undefined && filters.iv_rank_min > 0) {
      mergedFilters.min_iv_rank = filters.iv_rank_min;
    }
    if (filters.iv_rank_max !== undefined && filters.iv_rank_max < 100) {
      mergedFilters.max_iv_rank = filters.iv_rank_max;
    }

    // RSI
    if (filters.rsi_min !== undefined && filters.rsi_min > 0) {
      mergedFilters.min_rsi = filters.rsi_min;
    }
    if (filters.rsi_max !== undefined && filters.rsi_max < 100) {
      mergedFilters.max_rsi = filters.rsi_max;
    }

    // Net Margin
    if (filters.net_margin_min !== undefined && filters.net_margin_min > -50) {
      mergedFilters.min_net_margin = filters.net_margin_min;
    }
    if (filters.net_margin_max !== undefined && filters.net_margin_max < 50) {
      mergedFilters.max_net_margin = filters.net_margin_max;
    }

    // Debt/Equity
    if (filters.debt_equity_max !== undefined && filters.debt_equity_max < 5) {
      mergedFilters.max_debt_equity = filters.debt_equity_max;
    }

    // Annualized Yield
    if (filters.annualized_yield_min !== undefined && filters.annualized_yield_min > 0) {
      mergedFilters.min_annualized_yield = filters.annualized_yield_min;
    }
    if (filters.annualized_yield_max !== undefined && filters.annualized_yield_max < 100) {
      mergedFilters.max_annualized_yield = filters.annualized_yield_max;
    }

    // Dividend Yield
    if (filters.dividend_yield_min !== undefined && filters.dividend_yield_min > 0) {
      mergedFilters.min_dividend_yield = filters.dividend_yield_min;
    }
    if (filters.dividend_yield_max !== undefined && filters.dividend_yield_max < 10) {
      mergedFilters.max_dividend_yield = filters.dividend_yield_max;
    }

    // Sectors
    if (filters.sectors && filters.sectors.length > 0) {
      mergedFilters.sectors = filters.sectors.join(',');
    }

    // Market Cap Categories
    if (filters.market_cap_categories && filters.market_cap_categories.length > 0) {
      mergedFilters.market_cap_categories = filters.market_cap_categories.join(',');
    }

    // Earnings filters
    if (filters.earnings_within_days !== undefined && filters.earnings_within_days > 0) {
      mergedFilters.earnings_within_days = filters.earnings_within_days;
    }
    if (filters.exclude_earnings_within_days !== undefined && filters.exclude_earnings_within_days > 0) {
      mergedFilters.exclude_earnings_within_days = filters.exclude_earnings_within_days;
    }

    // Trend Signals
    if (filters.trend_signals && filters.trend_signals.length > 0) {
      mergedFilters.trend_filter = filters.trend_signals.join(',');
    }

    // Spread filter - update both mergedFilters and filterCriteria
    if (filters.spread_pct_max !== undefined && filters.spread_pct_max !== null) {
      mergedFilters.max_spread_pct = filters.spread_pct_max;
      setFilterCriteria(prev => ({ ...prev, maxSpreadPct: filters.spread_pct_max }));
    } else if (Object.keys(filters).length === 0) {
      // Reset to default spread filter when all filters are cleared (Reset All)
      setFilterCriteria(prev => ({ ...prev, maxSpreadPct: LIQUIDITY_RULE.maxSpreadPct }));
    }
    if (filters.spread_pct_min !== undefined && filters.spread_pct_min !== null && filters.spread_pct_min > 0) {
      mergedFilters.min_spread_pct = filters.spread_pct_min;
    }

    // Slope 12w
    if (filters.slope_12w_min !== undefined && filters.slope_12w_min > -50) {
      mergedFilters.min_slope_12w = filters.slope_12w_min;
    }
    if (filters.slope_12w_max !== undefined && filters.slope_12w_max < 50) {
      mergedFilters.max_slope_12w = filters.slope_12w_max;
    }
    // MACD signals
    if (filters.macd_signals && filters.macd_signals.length > 0) {
      mergedFilters.macd_signals = filters.macd_signals.join(',');
    }
    // Open Interest
    if (filters.open_interest_min !== undefined && filters.open_interest_min > 0) {
      mergedFilters.min_open_interest = filters.open_interest_min;
    }
    if (filters.open_interest_max !== undefined && filters.open_interest_max < OI_SLIDER_MAX) {
      mergedFilters.max_open_interest = filters.open_interest_max;
    }
    // Baseline P&L
    if (filters.baseline_pnl_min !== undefined && filters.baseline_pnl_min > -3) {
      mergedFilters.min_baseline_pnl = filters.baseline_pnl_min;
    }
    if (filters.baseline_pnl_max !== undefined && filters.baseline_pnl_max < 4) {
      mergedFilters.max_baseline_pnl = filters.baseline_pnl_max;
    }
    // Dimension stars
    if (filters.min_dimension1_stars !== undefined) {
      mergedFilters.min_dimension1_stars = filters.min_dimension1_stars;
    }
    if (filters.min_dimension2_stars !== undefined) {
      mergedFilters.min_dimension2_stars = filters.min_dimension2_stars;
    }
    // Setup signals
    if (filters.setup_signals && filters.setup_signals.length > 0) {
      mergedFilters.setup_signals = filters.setup_signals.join(',');
    }

    setBeginnerFilters(mergedFilters);
  }, []);

  // Debounced handler for real-time filter changes from sidebar (desktop)
  const handleSidebarFilterChange = useDebouncedCallback((filters: AdvancedFilters) => {
    handleApplyAdvancedFilters(filters);

    // Skip modification detection if we're currently applying a preset
    if (isApplyingPresetRef.current) {
      return;
    }

    // If we have a base preset and filters changed, mark as modified
    if (basePresetId && !isPresetModified) {
      setIsPresetModified(true);
      setActivePresetId(null); // Clear active preset, but keep basePresetId
    }
  }, 300);

  // Convert current filters to AdvancedFilters format for modal pre-population
  const getInitialAdvancedFilters = useCallback((): AdvancedFilters => {
    return {
      iv_rank_min: beginnerFilters.min_iv_rank ?? advancedFilters.iv_rank_min,
      iv_rank_max: beginnerFilters.max_iv_rank ?? advancedFilters.iv_rank_max,
      rsi_min: beginnerFilters.min_rsi ?? advancedFilters.rsi_min,
      rsi_max: beginnerFilters.max_rsi ?? advancedFilters.rsi_max,
      net_margin_min: beginnerFilters.min_net_margin ?? advancedFilters.net_margin_min,
      debt_equity_max: beginnerFilters.max_debt_equity ?? advancedFilters.debt_equity_max,
      trend_signals: beginnerFilters.trend_filter?.split(',') ?? advancedFilters.trend_signals,
      ...advancedFilters
    };
  }, [beginnerFilters, advancedFilters]);

  // Handler for removing individual advanced filter
  const handleRemoveAdvancedFilter = useCallback((filterKey: keyof AdvancedFilters) => {
    setAdvancedFilters(prev => {
      const updated = { ...prev };

      // Handle range filters (need to clear both min and max)
      if (filterKey === 'iv_rank_min') {
        delete updated.iv_rank_min;
        delete updated.iv_rank_max;
      } else if (filterKey === 'annualized_yield_min') {
        delete updated.annualized_yield_min;
        delete updated.annualized_yield_max;
      } else if (filterKey === 'net_margin_min') {
        delete updated.net_margin_min;
        delete updated.net_margin_max;
      } else if (filterKey === 'dividend_yield_min') {
        delete updated.dividend_yield_min;
        delete updated.dividend_yield_max;
      } else if (filterKey === 'rsi_min') {
        delete updated.rsi_min;
        delete updated.rsi_max;
      } else if (filterKey === 'spread_pct_max') {
        delete updated.spread_pct_min;
        delete updated.spread_pct_max;
      } else if (filterKey === 'baseline_pnl_min') {
        delete updated.baseline_pnl_min;
        delete updated.baseline_pnl_max;
      } else if (filterKey === 'slope_12w_min') {
        delete updated.slope_12w_min;
        delete updated.slope_12w_max;
      } else if (filterKey === 'open_interest_min') {
        delete updated.open_interest_min;
        delete updated.open_interest_max;
      } else {
        delete updated[filterKey];
      }

      return updated;
    });

    // Handle spread filter - also clear from filterCriteria
    if (filterKey === 'spread_pct_max') {
      setFilterCriteria(prev => ({ ...prev, maxSpreadPct: undefined }));
    }

    // Also clear from beginner filters
    setBeginnerFilters((prev: any) => {
      const updated = { ...prev };
      if (filterKey === 'iv_rank_min') {
        delete updated.min_iv_rank;
        delete updated.max_iv_rank;
      } else if (filterKey === 'rsi_min') {
        delete updated.min_rsi;
        delete updated.max_rsi;
      } else if (filterKey === 'net_margin_min') {
        delete updated.min_net_margin;
        delete updated.max_net_margin;
      } else if (filterKey === 'debt_equity_max') {
        delete updated.max_debt_equity;
      } else if (filterKey === 'annualized_yield_min') {
        delete updated.min_annualized_yield;
        delete updated.max_annualized_yield;
      } else if (filterKey === 'dividend_yield_min') {
        delete updated.min_dividend_yield;
        delete updated.max_dividend_yield;
      } else if (filterKey === 'sectors') {
        delete updated.sectors;
      } else if (filterKey === 'market_cap_categories') {
        delete updated.market_cap_categories;
      } else if (filterKey === 'trend_signals') {
        delete updated.trend_filter;
      } else if (filterKey === 'spread_pct_max') {
        delete updated.max_spread_pct;
        delete updated.min_spread_pct;
      } else if (filterKey === 'exclude_earnings_within_days') {
        delete updated.exclude_earnings_within_days;
      } else if (filterKey === 'earnings_within_days') {
        delete updated.earnings_within_days;
      } else if (filterKey === 'min_dimension1_stars') {
        delete updated.min_dimension1_stars;
      } else if (filterKey === 'min_dimension2_stars') {
        delete updated.min_dimension2_stars;
      } else if (filterKey === 'baseline_pnl_min') {
        delete updated.min_baseline_pnl;
        delete updated.max_baseline_pnl;
      } else if (filterKey === 'slope_12w_min') {
        delete updated.min_slope_12w;
        delete updated.max_slope_12w;
      } else if (filterKey === 'open_interest_min') {
        delete updated.min_open_interest;
        delete updated.max_open_interest;
      } else if (filterKey === 'setup_signals') {
        delete updated.setup_signals;
      } else if (filterKey === 'macd_signals') {
        delete updated.macd_signals;
      }
      return updated;
    });
  }, []);

  // Handler for clearing all filters (used in empty state message)
  // Keeps search term so the searched symbol remains visible after clearing filters
  const handleClearAllFilters = useCallback(() => {
    setAdvancedFilters({});
    setBeginnerFilters({});
    setFilterCriteria(prev => ({ ...prev, maxSpreadPct: undefined }));
    // Don't clear searchTerm/searchInput - keep the user's search so their symbol shows
    setActivePresetId(null);
    setBasePresetId(null);
    setIsPresetModified(false);
  }, []);

  // Check if any filters are active (for showing empty state vs stock list)
  const hasActiveFilters = useMemo(() => {
    // Has preset selected
    if (activePresetId !== null) return true;

    // Has search term
    if (searchTerm.trim() !== '') return true;

    // Check advancedFilters for any non-default values
    const hasAdvanced = Object.entries(advancedFilters).some(([key, value]) => {
      if (value === undefined || value === null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });
    if (hasAdvanced) return true;

    // Check beginnerFilters for any values
    const hasBeginnerFilters = Object.entries(beginnerFilters).some(([key, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    });

    return hasBeginnerFilters;
  }, [activePresetId, searchTerm, advancedFilters, beginnerFilters]);

  // Memoized active preset object for ScreenerHeader
  const activePreset = useMemo(() => {
    if (!activePresetId) return null;
    return systemPresets.find(p => p.id === activePresetId) || null;
  }, [activePresetId, systemPresets]);

  // Memoized base preset object (for modified state)
  const basePreset = useMemo(() => {
    if (!basePresetId) return null;
    return systemPresets.find(p => p.id === basePresetId) || null;
  }, [basePresetId, systemPresets]);

  // Handler for selecting a preset (from sidebar or elsewhere)
  const handlePresetSelect = useCallback((presetId: number | null) => {
    // Set flag to skip modification detection during preset application
    isApplyingPresetRef.current = true;

    setActivePresetId(presetId);

    if (presetId !== null) {
      // Store as base preset for modification tracking
      setBasePresetId(presetId);
      setIsPresetModified(false);
    } else {
      // User selected "Custom" - clear tracking
      setBasePresetId(null);
      setIsPresetModified(false);
    }

    // Clear the flag after debounce completes (300ms debounce + buffer)
    setTimeout(() => {
      isApplyingPresetRef.current = false;
    }, 400);
  }, []);

  const filterSuggestions = async (input: string) => {
    if (!input || input.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // Use API endpoint for symbols with options
      const response = await apiService.getSymbolsWithOptions(input, 15);
      
      if (response.status === 'success') {
        const dynamicResults = response.symbols.map((item) => ({
          symbol: item.symbol,
          name: item.name
        }));
        setSuggestions(dynamicResults);
        setShowSuggestions(dynamicResults.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (ticker: {symbol: string; name: string}) => {
    setSearchInput(ticker.symbol); // Update input display
    // Clear all filters so the searched stock always appears
    setAdvancedFilters({});
    setBeginnerFilters({});
    setFilterCriteria(prev => ({ ...prev, maxSpreadPct: undefined }));
    setActivePresetId(null);
    setBasePresetId(null);
    setIsPresetModified(false);
    setSearchTerm(ticker.symbol);  // Trigger filtering
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  // Load user's column preferences from localStorage.
  // Auto-merge any newly-added default columns so users see new defaults
  // (e.g. DTE, Spread %, Setup) without losing their custom selections.
  useEffect(() => {
    const savedColumns = localStorage.getItem('stockScreenerColumns');
    const seenDefaultsRaw = localStorage.getItem('stockScreenerSeenDefaults');
    const currentDefaults = COLUMN_DEFINITIONS.filter(c => c.isDefault).map(c => c.id);

    let seenDefaults: string[] = [];
    try { seenDefaults = seenDefaultsRaw ? JSON.parse(seenDefaultsRaw) : []; } catch { /* noop */ }

    if (savedColumns) {
      try {
        const parsed: string[] = JSON.parse(savedColumns);
        // New defaults the user has never seen yet — merge them in
        const newDefaults = currentDefaults.filter(id => !seenDefaults.includes(id) && !parsed.includes(id));
        if (newDefaults.length > 0) {
          setVisibleColumns([...parsed, ...newDefaults]);
        } else {
          setVisibleColumns(parsed);
        }
      } catch (error) {
        console.error('Failed to parse saved columns:', error);
      }
    }
    // Remember which defaults this user has been shown so we don't re-add ones they explicitly removed.
    localStorage.setItem('stockScreenerSeenDefaults', JSON.stringify(currentDefaults));

    // Beta: beginner mode is hardcoded, no need to listen for changes
  }, []);

  // Read symbol from URL parameters (e.g., from Dashboard "Analyze Stock" action)
  useEffect(() => {
    const symbolParam = searchParams.get('symbol');
    if (symbolParam) {
      const upperSymbol = symbolParam.toUpperCase();
      setSearchInput(upperSymbol);
      // Clear all filters so the searched stock always appears
      setAdvancedFilters({});
      setBeginnerFilters({});
      setFilterCriteria(prev => ({ ...prev, maxSpreadPct: undefined }));
      setActivePresetId(null);
      setBasePresetId(null);
      setIsPresetModified(false);
      setSearchTerm(upperSymbol);
    }
  }, [searchParams]);

  // Update progress step based on wizard state (when in beginner mode)
  useEffect(() => {
    if (isBeginnerMode) {
      if (wizardCompleted) {
        setStep(4); // Wizard complete, user should pick a stock
      } else {
        // Wizard not completed - let BeginnerFlowWizard control steps 1-3
        // Don't override, but ensure we're not on step 5
        const currentProgress = JSON.parse(localStorage.getItem('beginner_progress_state') || '{"currentStep":1}');
        if (currentProgress.currentStep > 3) {
          setStep(1); // Reset to step 1 if somehow we're beyond wizard steps
        }
      }
    }
  }, [isBeginnerMode, wizardCompleted, setStep]);

  // Save column preferences when they change
  useEffect(() => {
    localStorage.setItem('stockScreenerColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Don't load on mount - wait for filters/presets to be applied
  // Load data on mount - no more empty state
  useEffect(() => {
    console.log('📊 Initial mount - loading stock data');
    loadStocksPage(1, false, sortBy, sortDirection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when wizard completes with filters (not on skip/no filters)
  useEffect(() => {
    if (isBeginnerMode && wizardCompleted) {
      const hasFilters = Object.keys(beginnerFilters).length > 0;
      if (hasFilters) {
        console.log('✅ Wizard completed with filters - loading stocks');
        loadStocks();
      } else {
        console.log('✅ Wizard completed (no filters) - showing empty state');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardCompleted]);

  // Batch load chart data for beginner mode (performance optimization)
  useEffect(() => {
    const fetchBatchChartData = async () => {
      // Only fetch batch data in beginner mode when we have stocks to display
      if (!isBeginnerMode || displayedStocks.length === 0 || isLoading) {
        return;
      }

      try {
        const symbols = displayedStocks.map(stock => stock.symbol);
        console.log(`📊 Fetching batch chart data for ${symbols.length} symbols...`);

        // Calculate 6-month date range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(endDate.getMonth() - 6);

        const fromDate = startDate.toISOString().split('T')[0];
        const toDate = endDate.toISOString().split('T')[0];

        // Fetch batch data with daily timespan
        const startTime = performance.now();
        const response = await apiService.getStockDataBatch(
          symbols,
          'day',
          fromDate,
          toDate
        );
        const elapsed = performance.now() - startTime;

        if (response.status === 'success') {
          const totalBars = Object.values(response.data).reduce((sum, d: any) => sum + (d.results?.length || 0), 0);
          setChartDataCache(response.data);
          console.log(`✅ Batch chart data loaded: ${symbols.length} symbols, ${totalBars} bars in ${elapsed.toFixed(0)}ms`);
        }
      } catch (error) {
        console.error('❌ Failed to fetch batch chart data:', error);
        // Don't show error to user - charts will fall back to individual fetching
      }
    };

    fetchBatchChartData();
  }, [displayedStocks, isBeginnerMode, isLoading]); // Re-fetch when stocks change

  // When filters change, reload page 1 with new filters
  // Note: This should NOT trigger when sortBy/sortDirection changes to avoid duplicate API calls
  useEffect(() => {
    // Skip initial render - mount effect handles initial load
    if (isFilterEffectInitialRender.current) {
      isFilterEffectInitialRender.current = false;
      return;
    }
    // Skip during sorting operations
    if (!isSortingRef.current) {
      setCurrentPage(1);
      loadStocksPage(1, false, sortBy, sortDirection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterCriteria, beginnerFilters, advancedFilters, strategy, activeScoringFramework?.id]); // Reload when any filter, the CC/CSP strategy, or the active scoring framework changes (frameworks load after first paint)
  
  // Note: Client-side filtering is removed - all filtering should be done server-side
  // For now, we'll show all loaded stocks until server-side filtering is implemented

  const loadStocks = async (forceRefresh: boolean = false) => {
    loadStocksPage(currentPage, forceRefresh);
  };
  
  const loadStocksPage = async (page: number = 1, forceRefresh: boolean = false, sortColumn: string = sortBy, sortOrder: 'asc' | 'desc' = sortDirection) => {
    // Create unique request identifier to prevent duplicate calls
    const offset = (page - 1) * itemsPerPage;

    // Create a fingerprint of current filters for duplicate detection
    // This ensures filter changes (via presets or manual) trigger new API calls
    const filterFingerprint = JSON.stringify({
      searchTerm,
      ...filterCriteria,
      ...beginnerFilters,
      ...advancedFilters,
      frameworkId: activeScoringFramework?.id || null,
      strategy,
    });
    const requestKey = `${page}-${itemsPerPage}-${sortColumn}-${sortOrder}-${forceRefresh}-${filterFingerprint}`;

    // Enhanced duplicate prevention for React StrictMode and concurrent calls
    if (!forceRefresh && lastRequestRef.current === requestKey) {
      if (isLoading || activeRequestRef.current) {
        console.log(`⚡ Skipping duplicate request: ${requestKey}`);
        return;
      }
      // Additional StrictMode protection - block rapid successive calls
      if (strictModeBlockerRef.current) {
        console.log(`🛑 StrictMode duplicate blocked: ${requestKey}`);
        return;
      }
    }
    
    lastRequestRef.current = requestKey;
    strictModeBlockerRef.current = true;
    
    // Clear any pending timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`📡 Loading page ${page} with ${itemsPerPage} items, offset ${offset}, sorting by ${sortColumn} ${sortOrder}`);

      // Build filters object based on mode
      let filters: any = undefined;

      if (isBeginnerMode) {
        // Pass beginner filters and add search term if present
        filters = { ...beginnerFilters };
        if (searchTerm) {
          filters.search = searchTerm;
        }
        // Apply spread filter if enabled
        if (filterCriteria.maxSpreadPct !== undefined) {
          filters.max_spread_pct = filterCriteria.maxSpreadPct;
        }
      } else {
        // Build advanced mode filters from filterCriteria and searchTerm
        filters = {};

        // Map advanced mode filter criteria to API parameters
        if (filterCriteria.minCCScore > 0) {
          filters.min_cc_score = filterCriteria.minCCScore;
        }
        if (filterCriteria.minAnnualizedYield > 0) {
          filters.min_annualized_yield = filterCriteria.minAnnualizedYield;
        }
        if (filterCriteria.maxRSI < 100) {
          filters.max_rsi = filterCriteria.maxRSI;
        }
        if (filterCriteria.minIVRank > 0) {
          filters.min_iv_rank = filterCriteria.minIVRank;
        }
        if (filterCriteria.maxIVRank < 100) {
          filters.max_iv_rank = filterCriteria.maxIVRank;
        }
        if (filterCriteria.sectorFilter !== 'all') {
          filters.sector_filter = filterCriteria.sectorFilter;
        }
        if (filterCriteria.marketCapFilter !== 'all') {
          filters.market_cap_filter = filterCriteria.marketCapFilter;
        }
        if (searchTerm) {
          filters.search = searchTerm;
        }
        // Apply spread filter if enabled
        if (filterCriteria.maxSpreadPct !== undefined) {
          filters.max_spread_pct = filterCriteria.maxSpreadPct;
        }

        // If no filters are set, pass undefined to avoid empty object
        if (Object.keys(filters).length === 0) {
          filters = undefined;
        }
      }

      // Inject custom framework ID if active
      if (activeScoringFramework) {
        if (!filters) filters = {};
        filters.framework_id = activeScoringFramework.id;
      }

      // Strategy lens (cc | csp): the backend serves the lens's score, yield,
      // contract, caps and liquidity (gold phase 2b); nothing is remapped here.
      if (strategy === 'csp') {
        if (!filters) filters = {};
        filters.strategy = 'csp';
      }

      const response = await apiService.getStockScreenerData(itemsPerPage, offset, forceRefresh, sortColumn, sortOrder, filters);

      // Guard against stale responses: if a newer request was fired while
      // this one was in flight, discard the result so it doesn't overwrite
      // the newer data (race condition fix).
      if (lastRequestRef.current !== requestKey) {
        console.log(`🕐 Discarding stale response for: ${requestKey.substring(0, 60)}…`);
        return;
      }

      if (response.status === 'success') {
        const stocks: StockScreenerData[] = response.stocks;
        setDisplayedStocks(stocks);
        setTotalStocks(response.total ?? 0);

        console.log(`✅ Loaded page ${page}: ${response.stocks.length}/${itemsPerPage} stocks (total: ${response.total})`);

        // Set dataset-level timestamp from API response
        // The newest Price time of the screened stocks (gold.stock_mark); none -> no time shown.
        setLastDataUpdate(response.last_updated ?? null);
        
        // Initialize enrichment progress from current data
        // setEnrichmentProgress({
        //   enriched: response.total || 0,
        //   total: response.total || 0,
        //   percentage: 100
        // });
      } else {
        console.error('❌ Failed to load stock data:', response);
        setError('Failed to load stock data');
        setDisplayedStocks([]);
        setTotalStocks(0);
      }
      
    } catch (error) {
      console.error('❌ Failed to load stock data:', error);
      setError(`Failed to load stock data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setDisplayedStocks([]);
      setTotalStocks(0);
    } finally {
      setIsLoading(false);
      activeRequestRef.current = null;
      // Reset StrictMode blocker after a short delay
      setTimeout(() => {
        strictModeBlockerRef.current = false;
      }, 100);
    }
  };
  
  const handleSort = (columnId: string) => {
    // Prevent multiple concurrent sort operations
    if (isSortingRef.current) {
      return;
    }
    
    isSortingRef.current = true;
    const newDirection = (sortBy === columnId && sortDirection === 'desc') ? 'asc' : 'desc';
    setSortBy(columnId);
    setSortDirection(newDirection);
    setCurrentPage(1); // Reset to first page when sorting changes
    
    // Trigger server-side sorted data reload
    loadStocksPage(1, false, columnId, newDirection).finally(() => {
      isSortingRef.current = false;
    });
  };

  // Explicit sort for the cards' "Sort by" control (table headers keep handleSort's toggle).
  // One sort key per figure; the backend applies it to the lens requested.
  const CARD_SORTS: Array<{ id: string; label: string; key: string }> = [
    { id: 'score', label: 'Score', key: 'score' },
    { id: 'premium', label: 'Premium', key: 'best_ann_yield' },
    { id: 'net', label: 'Setup P&L', key: 'setup_pnl' },
    { id: 'timing', label: 'Timing', key: 'timing' },
    { id: 'ivr', label: 'IV rank', key: 'iv_rank' },
  ];
  const activeCardSort = CARD_SORTS.find((o) => o.key === sortBy || (o.id === 'score' && ['cc_score', 'csp_score', 'cc_rank'].includes(sortBy)))?.id ?? '';
  const applyCardSort = (id: string) => {
    const opt = CARD_SORTS.find((o) => o.id === id);
    if (!opt || isSortingRef.current) return;
    isSortingRef.current = true;
    setSortBy(opt.key);
    setSortDirection('desc');
    setCurrentPage(1);
    loadStocksPage(1, false, opt.key, 'desc').finally(() => { isSortingRef.current = false; });
  };

  // Removed getMarketCapValue function - now using market_cap_value field directly

  // Latest price (delayed) chip: shown next to the Price, never replaces it
  // (glossary stock_price_live). Polled every 60 s while the tab is visible.
  const latestSymbols = useMemo(() => displayedStocks.map(s => s.symbol), [displayedStocks]);
  const latestQuotes = useLatestPrices(latestSymbols);

  // No pagination logic needed - data comes pre-paginated from server
  const sortedAndPaginatedStocks = React.useMemo(() => {
    return {
      data: displayedStocks,
      totalItems: totalStocks,
      totalPages: Math.ceil(totalStocks / itemsPerPage)
    };
  }, [displayedStocks, totalStocks, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Load the new page from server
    loadStocksPage(page, false, sortBy, sortDirection);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric', 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatCellValue = (value: any, format?: string) => {
    // Handle null, undefined, empty string, or falsy values
    if (value == null || value === undefined || value === '') return 'N/A';
    
    switch (format) {
      case 'currency':
        const currencyNum = Number(value);
        if (isNaN(currencyNum) || !isFinite(currencyNum)) return 'N/A';
        if (currencyNum < 0) return `-$${Math.abs(currencyNum).toFixed(2)}`;
        if (currencyNum >= 1000000) return `$${(currencyNum / 1000000).toFixed(2)}M`;
        return `$${currencyNum.toFixed(2)}`;
      
      case 'percentage':
        const percentNum = Number(value);
        if (isNaN(percentNum) || !isFinite(percentNum)) return 'N/A';
        if (Math.abs(percentNum) >= 1000) return `${percentNum > 0 ? '+' : ''}${percentNum.toFixed(0)}%`;
        return `${percentNum >= 0 ? '+' : ''}${percentNum.toFixed(1)}%`;
      
      case 'number':
        const numberNum = Number(value);
        if (isNaN(numberNum) || !isFinite(numberNum)) return 'N/A';
        if (Math.abs(numberNum) >= 1000) return numberNum.toFixed(0);
        return numberNum.toFixed(2);
      
      case 'score':
        const scoreNum = Number(value);
        if (isNaN(scoreNum) || !isFinite(scoreNum)) return 'N/A';
        if (scoreNum < 0) return '0.0';
        if (scoreNum > 100) return '100.0';
        return scoreNum.toFixed(1);
      
      case 'volume':
        const volumeNum = Number(value);
        if (isNaN(volumeNum) || !isFinite(volumeNum) || volumeNum < 0) return 'N/A';
        if (volumeNum >= 1000000) {
          const millions = volumeNum / 1000000;
          return `${millions.toFixed(1)}M`;
        } else if (volumeNum >= 1000) {
          const thousands = volumeNum / 1000;
          return `${thousands.toFixed(0)}K`;
        }
        return volumeNum.toString();
      
      case 'text':
      default:
        // Ensure we have a valid string and handle edge cases
        const stringValue = String(value).trim();
        if (stringValue === 'null' || stringValue === 'undefined') return 'N/A';
        return stringValue || 'N/A';
    }
  };

  const getCCScoreIcon = (score: number) => {
    if (score >= 40) return { icon: '⭐', color: 'text-yellow-400' };  // 40-41
    if (score >= 36) return { icon: '🟢', color: 'text-green-400' };  // 36-39
    if (score >= 26) return { icon: '🟡', color: 'text-yellow-400' }; // 26-35
    if (score >= 19) return { icon: '🟠', color: 'text-orange-400' }; // 19-25
    return { icon: '🔴', color: 'text-red-400' }; // 14-18
  };


  const getRSIIcon = (rsi: number) => {
    if (rsi >= 70) return { icon: '🟢', color: 'text-green-400' }; // Overbought
    if (rsi >= 50) return { icon: '🟡', color: 'text-yellow-400' }; // Upward momentum
    if (rsi >= 30) return { icon: '🟠', color: 'text-orange-400' }; // Neutral
    return { icon: '🔴', color: 'text-red-400' }; // Oversold
  };

  // Preset functions
  const applyPreset = (presetName: 'essential' | 'professional') => {
    let columns: string[] = [];
    
    switch (presetName) {
      case 'essential':
        columns = ['symbol', 'company_name', 'current_price', 'cc_score', 'best_ann_yield', 'iv_percentile', 'rsi', 'sector'];
        break;
      case 'professional':
        columns = COLUMN_DEFINITIONS.map(col => col.id);
        break;
    }
    
    setVisibleColumns(columns);
  };

  const visibleColumnDefs = COLUMN_DEFINITIONS.filter(col => 
    visibleColumns.includes(col.id)
  );

  return (
    <Layout>
      <div className="app-page-container">
        
        {/* Single-column layout for beginner mode with wizard */}
        {isBeginnerMode ? (
          <div className="beginner-mode-layout-accordion">
            {/* Wizard Prompt Banner */}
            {showWizardBanner && (
              <WizardPromptBanner
                onStartWizard={() => setShowWizardModal(true)}
                onDismiss={handleBannerDismiss}
              />
            )}

            {/* Two-Column Layout: Filter Sidebar + Results */}
            <div className={`screener-layout ${filtersHidden && isWideScreen ? 'is-filters-hidden' : ''}`}>
              {/* Filter Sidebar (desktop) / FAB (mobile) */}
              <FilterSidebarWrapper
                filters={advancedFilters}
                onChange={handleSidebarFilterChange}
                resultCount={totalStocks}
                isLoadingCount={isLoading}
                activePresetId={activePresetId}
                onSelectPreset={handlePresetSelect}
                onSavePreset={() => setShowSavePresetModal(true)}
                isPresetModified={isPresetModified}
                basePresetId={basePresetId}
              />

              {/* Content Area */}
              <div className="screener-content">
                {/* Sticky Header: Search + Filter Pills */}
                <div className="screener-content-sticky">
                  {/* Two-step flow: this page ranks stocks; contracts are picked on the next page */}
                  {/* Row 1: step + strategy (what you are picking) | result count + data time */}
                  <div className="screener-row1">
                    <div className="flow-step">
                      <span className="flow-step-n">Step 1</span>
                      <span className="flow-step-t">Pick the stock</span>
                      <StrategyToggle className="is-lg" />
                    </div>
                    <span className="screener-row1-meta">
                      <span className="screener-stock-count">{totalStocks} stocks</span>
                      {lastDataUpdate && <span className="screener-data-timestamp">data {formatTimestamp(lastDataUpdate)}</span>}
                    </span>
                  </div>
                  {/* Search Row */}
                  <div className="screener-search-row">
                    <div className="app-search" style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setSearchInput(value);

                          // If input is cleared, also clear the filter immediately
                          if (value === '') {
                            setSearchTerm('');
                          }

                          // Clear previous timeout
                          if (searchTimeoutRef.current) {
                            clearTimeout(searchTimeoutRef.current);
                          }

                          // Debounce the autocomplete suggestions
                          searchTimeoutRef.current = setTimeout(() => {
                            filterSuggestions(value);
                          }, 300);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (showSuggestions && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                              // User selected a suggestion with Enter
                              selectSuggestion(suggestions[highlightedIndex]);
                            } else {
                              // User pressed Enter without selecting - clear filters and apply search
                              setAdvancedFilters({});
                              setBeginnerFilters({});
                              setFilterCriteria(prev => ({ ...prev, maxSpreadPct: undefined }));
                              setActivePresetId(null);
                              setBasePresetId(null);
                              setIsPresetModified(false);
                              setSearchTerm(searchInput);
                              setShowSuggestions(false);
                            }
                          } else if (showSuggestions) {
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              setHighlightedIndex(prev =>
                                prev < suggestions.length - 1 ? prev + 1 : prev
                              );
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                            } else if (e.key === 'Escape') {
                              setShowSuggestions(false);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          // Delay hiding suggestions to allow clicking
                          setTimeout(() => {
                            setShowSuggestions(false);
                          }, 150);
                        }}
                        onFocus={() => {
                          if (searchInput.length > 0) {
                            // Clear any pending search timeout
                            if (searchTimeoutRef.current) {
                              clearTimeout(searchTimeoutRef.current);
                            }
                            filterSuggestions(searchInput);
                          }
                        }}
                        placeholder="Search symbols..."
                        className="app-search-input"
                      />
                      {searchInput && (
                        <button
                          onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                          style={{
                            position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                            padding: '2px', background: 'transparent', border: 'none',
                            color: 'var(--color-text-muted)', cursor: 'pointer', zIndex: 2,
                            display: 'flex', alignItems: 'center',
                          }}
                          title="Clear search"
                        >
                          <XMarkIcon style={{ width: 16, height: 16 }} />
                        </button>
                      )}
                      {showSuggestions && suggestions.length > 0 && (
                        <div className="app-search-suggestions">
                          {suggestions.map((ticker, index) => (
                            <div
                              key={ticker.symbol}
                              className={`app-search-suggestion ${
                                index === highlightedIndex ? 'highlighted' : ''
                              }`}
                              onClick={() => selectSuggestion(ticker)}
                            >
                              <span className="app-suggestion-symbol">{ticker.symbol}</span>
                              <span className="app-suggestion-name">{ticker.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Row 2 (right): layout controls */}
                    <div className="screener-stock-info">
                      {isWideScreen && (
                        <button type="button" className="screener-filters-toggle" aria-pressed={!filtersHidden} onClick={toggleFilters} title={filtersHidden ? 'Show filters' : 'Hide filters'}>
                          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" /></svg>
                          {filtersHidden ? 'Show filters' : 'Hide filters'}
                        </button>
                      )}
                      {viewMode === 'cards' && (
                        <label className="screener-sort">
                          <span>Sort</span>
                          <select value={activeCardSort} onChange={(e) => applyCardSort(e.target.value)} aria-label="Sort cards by">
                            {activeCardSort === '' && <option value="">Custom</option>}
                            {CARD_SORTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                        </label>
                      )}
                      <div className="screener-view-toggle" role="group" aria-label="Result view">
                        <button type="button" aria-pressed={viewMode === 'cards'} onClick={() => changeViewMode('cards')}>Cards</button>
                        <button type="button" aria-pressed={viewMode === 'table'} onClick={() => changeViewMode('table')}>Table</button>
                      </div>
                      <button
                        onClick={() => loadStocks(false)}
                        disabled={isLoading}
                        className="btn-secondary screener-refresh-btn"
                      >
                        {isLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>
                  </div>

                  {/* Filter Pills */}
                  {hasActiveFilters && (
                    <div className="screener-pills-row">
                      <ActiveFilterPills
                        filters={advancedFilters}
                        onRemoveFilter={handleRemoveAdvancedFilter}
                      />
                    </div>
                  )}
                </div>

                {/* Scrollable Results */}
                <div className="screener-results-scroll">
                  {error && (
                    <div role="alert" style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.45)', color: '#fca5a5', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 500 }}>
                      {error}
                    </div>
                  )}

                  {/* Dynamic Header - Shows preset info when active */}
                  <ScreenerHeader
                    activePreset={activePreset}
                    basePreset={basePreset}
                    isModified={isPresetModified}
                    resultCount={totalStocks}
                    isLoading={isLoading}
                    onSaveAsPreset={() => setShowSavePresetModal(true)}
                  />

                  {/* Framework Selector */}
                  {scoringFrameworks.filter(f => f.user_id !== null).length > 0 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 12px', marginBottom: '12px', borderRadius: '6px',
                      background: 'var(--color-bg-secondary)', border: `1px solid ${activeScoringFramework ? '#3b82f644' : 'var(--color-bg-tertiary)'}`,
                      fontSize: '0.8rem',
                    }}>
                      <span style={{ color: 'var(--color-text-muted)' }}>Scoring:</span>
                      <select
                        value={activeScoringFramework?.id ?? 'default'}
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (val === 'default') {
                            if (activeScoringFramework) {
                              await apiService.deactivateFramework(activeScoringFramework.id);
                              setActiveScoringFramework(null);
                              await refreshFrameworks();
                            }
                          } else {
                            const fw = scoringFrameworks.find(f => f.id === Number(val));
                            if (fw) {
                              await apiService.activateFramework(fw.id);
                              setActiveScoringFramework(fw);
                              await refreshFrameworks();
                            }
                          }
                        }}
                        style={{
                          padding: '3px 8px', borderRadius: '4px',
                          background: 'var(--color-bg-tertiary)', border: '1px solid var(--color-gray-700)',
                          color: activeScoringFramework ? '#60a5fa' : 'var(--color-white)',
                          fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        <option value="default">CC Score v7.2 (Default)</option>
                        {scoringFrameworks.filter(f => f.user_id !== null).map(fw => (
                          <option key={fw.id} value={fw.id}>{fw.name}</option>
                        ))}
                      </select>
                      {activeScoringFramework && (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                          Q:{activeScoringFramework.quality_points} / T:{activeScoringFramework.timing_points}
                        </span>
                      )}
                    </div>
                  )}

                  {viewMode === 'table' ? (
                    <ScreenerTable
                      stocks={sortedAndPaginatedStocks.data}
                      latestQuotes={latestQuotes}
                      isCsp={strategy === 'csp'}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      onOpen={(st) => navigate(`/analysis?symbol=${st.symbol}`)}
                      isLoading={isLoading}
                      empty={
                        sortedAndPaginatedStocks.totalItems === 0 ? (
                          <>
                            No stocks match your selections. Try{' '}
                            <button
                              onClick={handleClearAllFilters}
                              style={{ color: '#7ea2ff', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                            >
                              removing the filters
                            </button>
                          </>
                        ) : (
                          'No stocks found. Try refreshing the data.'
                        )
                      }
                    />
                  ) : (
                  /* Stock Cards Grid */
                  <div className="stock-cards-grid">
                        {isLoading ? (
                          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 0' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div className="mini-stock-chart-spinner" style={{ margin: '0 auto 0.5rem' }}></div>
                              <p style={{ fontSize: '0.875rem', color: '#999999' }}>Loading stock data...</p>
                            </div>
                          </div>
                        ) : sortedAndPaginatedStocks.data.length === 0 ? (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem 0', color: '#888888' }}>
                            {sortedAndPaginatedStocks.totalItems === 0 ? (
                              <>
                                No stocks match your selections. Try{' '}
                                <button
                                  onClick={handleClearAllFilters}
                                  style={{ color: '#3b82f6', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                                >
                                  removing the filters
                                </button>
                              </>
                            ) : (
                              'No stocks found. Try refreshing the data.'
                            )}
                          </div>
                        ) : (
                          sortedAndPaginatedStocks.data.map((stock, i) => {
                            return (
                              <StockCard
                                key={stock.symbol}
                                rank={(currentPage - 1) * itemsPerPage + i + 1}
                                stock={stock}
                                chartData={chartDataCache[stock.symbol]}
                                wizardFilters={isBeginnerMode && beginnerFilterDetails?.activeFilters ? beginnerFilterDetails.activeFilters : undefined}
                                strategyType={isBeginnerMode && beginnerFilterDetails?.objective ? beginnerFilterDetails.objective : undefined}
                                lastUpdated={lastDataUpdate}
                                latest={latestQuotes.get(stock.symbol) ?? null}
                              />
                            );
                          })
                        )}
                      </div>
                  )}

                  {/* Pagination Controls */}
                  {sortedAndPaginatedStocks.totalPages > 1 && (
                    <div className="screener-pagination">
                      <div className="text-sm text-gray-400">
                        Page {currentPage} of {sortedAndPaginatedStocks.totalPages}
                        ({sortedAndPaginatedStocks.totalItems} total stocks)
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="btn-pagination"
                          title="First page"
                        >
                          First
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="btn-pagination"
                          title="Previous page"
                        >
                          ← Prev
                        </button>

                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, sortedAndPaginatedStocks.totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(
                            sortedAndPaginatedStocks.totalPages - 4,
                            currentPage - 2
                          )) + i;

                          if (pageNum > sortedAndPaginatedStocks.totalPages) return null;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={
                                pageNum === currentPage
                                  ? 'btn-pagination btn-pagination-active'
                                  : 'btn-pagination'
                              }
                            >
                              {pageNum}
                            </button>
                          );
                        })}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === sortedAndPaginatedStocks.totalPages}
                          className="btn-pagination"
                          title="Next page"
                        >
                          Next →
                        </button>
                        <button
                          onClick={() => handlePageChange(sortedAndPaginatedStocks.totalPages)}
                          disabled={currentPage === sortedAndPaginatedStocks.totalPages}
                          className="btn-pagination"
                          title="Last page"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div> {/* closes screener-results-scroll */}
              </div> {/* closes screener-content */}
            </div> {/* closes screener-layout */}

            {/* Floating AI Coach Button */}
            <button
              className="app-ai-coach-floating"
              onClick={() => navigate('/ai-coach')}
              title="AI Coach"
            >
              <SparklesIcon className="app-ai-coach-icon" />
              <span className="app-ai-coach-label">AI Coach</span>
            </button>
          </div>
        ) : (
          /* Original advanced mode layout */
          <div>
            {/* Header - aligned with Portfolio structure */}
            <div className="app-page-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div>
                  <h1 className="app-page-title">Stock Screener</h1>
                  <p className="app-page-subtitle">Screen stocks for {strategy === 'csp' ? 'cash-secured put' : 'covered call'} opportunities</p>
                </div>
                <DataDelayIndicator />
              </div>
            </div>

            {/* Stock Screener */}
        <div className="app-screener-filter" style={{ position: 'relative', overflow: 'visible' }}>
          {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-2" style={{ position: 'relative', zIndex: 100 }}>
              <div className="app-search" style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setSearchInput(value);

                    // If input is cleared, also clear the filter immediately
                    if (value === '') {
                      setSearchTerm('');
                    }

                    // Clear previous timeout
                    if (searchTimeoutRef.current) {
                      clearTimeout(searchTimeoutRef.current);
                    }

                    // Debounce the autocomplete suggestions
                    searchTimeoutRef.current = setTimeout(() => {
                      filterSuggestions(value);
                    }, 300);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showSuggestions && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                        // User selected a suggestion with Enter
                        selectSuggestion(suggestions[highlightedIndex]);
                      } else {
                        // User pressed Enter without selecting - clear filters and apply search
                        setAdvancedFilters({});
                        setBeginnerFilters({});
                        setFilterCriteria(prev => ({ ...prev, maxSpreadPct: undefined }));
                        setActivePresetId(null);
                        setBasePresetId(null);
                        setIsPresetModified(false);
                        setSearchTerm(searchInput);
                        setShowSuggestions(false);
                      }
                    } else if (showSuggestions) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setHighlightedIndex(prev =>
                          prev < suggestions.length - 1 ? prev + 1 : prev
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                      } else if (e.key === 'Escape') {
                        setShowSuggestions(false);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Delay hiding suggestions to allow clicking
                    setTimeout(() => {
                      setShowSuggestions(false);
                    }, 150);
                  }}
                  onFocus={() => {
                    if (searchInput.length > 0) {
                      // Clear any pending search timeout
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      filterSuggestions(searchInput);
                    }
                  }}
                  placeholder="Search symbols..."
                  className="app-search-input"
                />
                {searchInput && (
                  <button
                    onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      padding: '2px', background: 'transparent', border: 'none',
                      color: 'var(--color-text-muted)', cursor: 'pointer', zIndex: 2,
                      display: 'flex', alignItems: 'center',
                    }}
                    title="Clear search"
                  >
                    <XMarkIcon style={{ width: 16, height: 16 }} />
                  </button>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="app-search-suggestions">
                    {suggestions.map((ticker, index) => (
                      <div
                        key={ticker.symbol}
                        className={`app-search-suggestion ${
                          index === highlightedIndex ? 'highlighted' : ''
                        }`}
                        onClick={() => selectSuggestion(ticker)}
                      >
                        <span className="app-suggestion-symbol">{ticker.symbol}</span>
                        <span className="app-suggestion-name">{ticker.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="relative">
                <select
                  value={filterCriteria.minCCScore}
                  onChange={(e) => setFilterCriteria(prev => ({
                    ...prev,
                    minCCScore: parseFloat(e.target.value) || 0
                  }))}
                  className="px-3 py-2 text-sm app-screener-input rounded text-white focus:border-blue-500 focus:outline-none"
                  style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                >
                  <option value={0}>Babylon Rating: All</option>
                  <option value={20}>Rating ≥ 20</option>
                  <option value={35}>Rating ≥ 35</option>
                  <option value={40}>Rating ≥ 40 (top tier)</option>
                  <option value={45}>Rating ≥ 45</option>
                </select>
              </div>
              
              <div className="relative">
                <select
                  value={filterCriteria.minAnnualizedYield}
                  onChange={(e) => setFilterCriteria(prev => ({
                    ...prev,
                    minAnnualizedYield: parseFloat(e.target.value) || 0
                  }))}
                  className="px-3 py-2 text-sm app-screener-input rounded text-white focus:border-blue-500 focus:outline-none"
                  style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                >
                  <option value={0}>Yield: All</option>
                  <option value={5}>Yield ≥ 5%</option>
                  <option value={10}>Yield ≥ 10%</option>
                  <option value={15}>Yield ≥ 15%</option>
                  <option value={20}>Yield ≥ 20%</option>
                </select>
              </div>
              
              <div className="relative">
                <select
                  value={filterCriteria.maxRSI}
                  onChange={(e) => setFilterCriteria(prev => ({
                    ...prev,
                    maxRSI: parseInt(e.target.value) || 100
                  }))}
                  className="px-3 py-2 text-sm app-screener-input rounded text-white focus:border-blue-500 focus:outline-none"
                  style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                >
                  <option value={100}>RSI: All</option>
                  <option value={80}>RSI ≤ 80</option>
                  <option value={70}>RSI ≤ 70</option>
                  <option value={60}>RSI ≤ 60</option>
                  <option value={50}>RSI ≤ 50</option>
                </select>
              </div>
              
              <div className="relative">
                <select
                  value={filterCriteria.sectorFilter}
                  onChange={(e) => setFilterCriteria(prev => ({
                    ...prev,
                    sectorFilter: e.target.value
                  }))}
                  className="px-3 py-2 text-sm app-screener-input rounded text-white focus:border-blue-500 focus:outline-none"
                  style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                >
                  <option value="all">Sector: All</option>
                  <option value="technology">Technology</option>
                  <option value="financial">Financial</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="known_sectors">Known Sectors Only</option>
                </select>
              </div>
              
              <div className="relative">
                <select
                  value={filterCriteria.minIVRank}
                  onChange={(e) => setFilterCriteria(prev => ({
                    ...prev,
                    minIVRank: parseFloat(e.target.value) || 0
                  }))}
                  className="px-3 py-2 text-sm app-screener-input rounded text-white focus:border-blue-500 focus:outline-none"
                  style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                >
                  <option value={0}>IV Rank: All</option>
                  <option value={30}>IV Rank ≥ 30%</option>
                  <option value={50}>IV Rank ≥ 50%</option>
                  <option value={70}>IV Rank ≥ 70%</option>
                  <option value={80}>IV Rank ≥ 80%</option>
                </select>
              </div>
              
              <div className="relative">
                <select
                  value={filterCriteria.marketCapFilter}
                  onChange={(e) => setFilterCriteria(prev => ({
                    ...prev,
                    marketCapFilter: e.target.value
                  }))}
                  className="px-3 py-2 text-sm app-screener-input rounded text-white focus:border-blue-500 focus:outline-none"
                  style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                >
                  <option value="all">Market Cap: All</option>
                  <option value="mega">Mega Cap (≥$1T)</option>
                  <option value="large">Large Cap ($10B-$1T)</option>
                  <option value="mid">Mid Cap ($2B-$10B)</option>
                  <option value="small">Small Cap ($300M-$2B)</option>
                </select>
              </div>
              
            </div>
            
            {/* Second Row - Quick Presets */}
            <div className="flex items-center gap-2 pt-4 mt-4 border-t border-gray-700">
              <span className="text-sm text-gray-400 mr-2">Quick Presets:</span>
              <div className="flex gap-0">
                <button
                  onClick={() => applyPreset('essential')}
                  className="px-3 py-2 text-sm font-medium text-white transition-colors"
                  style={{
                    backgroundColor: JSON.stringify(visibleColumns.sort()) === JSON.stringify(['symbol', 'company_name', 'current_price', 'cc_score', 'best_ann_yield', 'iv_percentile', 'rsi', 'sector'].sort())
                      ? '#4b5563'
                      : 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    outline: 'none',
                    borderRadius: JSON.stringify(visibleColumns.sort()) === JSON.stringify(['symbol', 'company_name', 'current_price', 'cc_score', 'best_ann_yield', 'iv_percentile', 'rsi', 'sector'].sort())
                      ? '6px'
                      : '0'
                  }}
                >
                  Essential
                </button>
                <button
                  onClick={() => applyPreset('professional')}
                  className="px-3 py-2 text-sm font-medium text-white transition-colors"
                  style={{
                    backgroundColor: JSON.stringify(visibleColumns.sort()) === JSON.stringify(COLUMN_DEFINITIONS.map(col => col.id).sort())
                      ? '#4b5563'
                      : 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    outline: 'none',
                    borderRadius: JSON.stringify(visibleColumns.sort()) === JSON.stringify(COLUMN_DEFINITIONS.map(col => col.id).sort())
                      ? '6px'
                      : '0'
                  }}
                >
                  Professional
                </button>
              </div>
            </div>
        </div>

          {/* Results Header */}
          <div className="app-card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <h3 className="app-card-title">{strategy === 'csp' ? 'Cash-Secured Put Candidates' : 'Covered Call Candidates'}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span>
                    {totalStocks} stocks
                  </span>
                  {lastDataUpdate && (
                    <span className="text-xs">
                      Data as of: {formatTimestamp(lastDataUpdate)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadStocks(false)}
                  disabled={isLoading}
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                >
                  {isLoading ? 'Loading...' : 'Refresh'}
                </button>
                
                {/* Right side - Rows selector */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-400">Rows:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = parseInt(e.target.value);
                      setItemsPerPage(newItemsPerPage);
                      setCurrentPage(1);
                      
                      // Debounce API call when changing page size
                      if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                      }
                      loadingTimeoutRef.current = setTimeout(() => {
                        loadStocksPage(1, false, sortBy, sortDirection);
                      }, 300);
                    }}
                    className="px-1 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white focus:border-blue-500 focus:outline-none w-14"
                    style={{ colorScheme: 'dark', backgroundColor: '#1f2937', color: 'white' }}
                  >
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div role="alert" style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.45)', color: '#fca5a5', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 500 }}>
                {error}
              </div>
            )}

            <div className="app-table-container-full-height">
              <div className="overflow-x-auto">
                <table className="app-options-table" style={{ minWidth: '1200px' }}>
                <thead className="app-table-header">
                  <tr>
                    {visibleColumnDefs.map((col) => (
                      <th 
                        key={col.id}
                        className={`app-table-th ${col.sortable ? 'app-sortable' : ''}`}
                        onClick={() => col.sortable && handleSort(col.id)}
                        style={{ width: col.width }}
                        title={col.description || undefined}
                      >
                        {col.label}
                        {col.sortable && sortBy === col.id && (
                          <span className="app-sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                    ))}
                    <th className="app-table-th" style={{ width: '40px' }}>
                      <button
                        onClick={() => setShowColumnSelector(!showColumnSelector)}
                        className="w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center justify-center transition-colors relative"
                        title="Add/Remove columns"
                      >
                        +
                        {showColumnSelector && (
                          <ColumnSelector
                            visibleColumns={visibleColumns}
                            onColumnsChange={setVisibleColumns}
                            onClose={() => setShowColumnSelector(false)}
                          />
                        )}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="app-table-body">
                  {isLoading ? (
                    <tr>
                      <td 
                        colSpan={visibleColumnDefs.length} 
                        className="app-table-cell text-center py-8"
                      >
                        Loading stock data...
                      </td>
                    </tr>
                  ) : sortedAndPaginatedStocks.data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleColumnDefs.length}
                        className="app-table-cell text-center py-8"
                        style={{ color: '#888888' }}
                      >
                        {sortedAndPaginatedStocks.totalItems === 0 ? (
                          <>
                            No stocks match your filters. Try{' '}
                            <button
                              onClick={handleClearAllFilters}
                              style={{ color: '#3b82f6', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                            >
                              removing the filters
                            </button>
                          </>
                        ) : (
                          'No stocks found. Try refreshing the data.'
                        )}
                      </td>
                    </tr>
                  ) : (
                    sortedAndPaginatedStocks.data.map((stock) => (
                      <tr 
                        key={stock.symbol}
                        className="app-table-row hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          // Navigate to stock analysis page
                          navigate(`/analysis?symbol=${stock.symbol}`);
                        }}
                      >
                        {visibleColumnDefs.map((col) => {
                          const value = stock[col.id as keyof StockScreenerData];
                          let cellClass = 'app-table-cell';
                          
                          // Center align numeric columns
                          if (col.format === 'currency' || col.format === 'percentage' || col.format === 'number' || col.format === 'score') {
                            cellClass += ' app-table-cell-numeric';
                          }
                          
                          // Special styling for certain columns
                          if (col.id === 'symbol') {
                            cellClass += ' font-medium text-blue-600 hover:text-blue-800';
                          }
                          
                          // No longer need enrichment indicator in symbol column since we have dedicated status column
                          
                          // Add loading indicator for options-related columns
                          const isOptionsColumn = ['cc_score', 'best_ann_yield', 'iv_percentile'].includes(col.id);
                          const showLoading = false; // isLoadingOptions && isOptionsColumn && (value == null || value === 0);
                          
                          // Add text truncation for company name and sector
                          const shouldTruncate = ['company_name', 'sector'].includes(col.id);
                          const truncateStyle = shouldTruncate ? {
                            width: col.width,
                            minWidth: col.width,
                            maxWidth: col.width,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap' as const
                          } : {};
                          
                          return (
                            <td key={col.id} className={`${cellClass} ${showLoading ? 'animate-pulse bg-gray-800' : ''}`} style={truncateStyle} title={shouldTruncate ? String(value) : undefined}>
                              {showLoading ? (
                                <div className="flex items-center justify-center">
                                  <Spinner size="sm" layout="bare" label="Loading" />
                                </div>
                              ) : col.id === 'options_available' && typeof value === 'boolean' ? (
                                <span className="text-center">{value ? '✓' : '✗'}</span>
                              ) : col.id === 'cc_score' ? (
                                <div className="flex items-center justify-center">
                                  <ScoreChip value={Number(value)} size="sm" />
                                </div>
                              ) : col.id === 'rsi' ? (
                                <div className="flex items-center justify-center gap-2">
                                  <span className={getRSIIcon(Number(value)).color}>
                                    {formatCellValue(value, col.format)}
                                  </span>
                                  {getRSIIcon(Number(value)).icon && (
                                    <span className={getRSIIcon(Number(value)).color}>
                                      {getRSIIcon(Number(value)).icon}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                formatCellValue(value, col.format)
                              )}
                            </td>
                          );
                        })}
                        <td className="app-table-cell" style={{ width: '40px' }}></td>
                      </tr>
                    ))
                  )}
                </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {sortedAndPaginatedStocks.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  Page {currentPage} of {sortedAndPaginatedStocks.totalPages} 
                  ({sortedAndPaginatedStocks.totalItems} total stocks)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="btn-pagination"
                    title="First page"
                  >
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn-pagination"
                    title="Previous page"
                  >
                    ← Prev
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, sortedAndPaginatedStocks.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(
                      sortedAndPaginatedStocks.totalPages - 4,
                      currentPage - 2
                    )) + i;
                    
                    if (pageNum > sortedAndPaginatedStocks.totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={
                          pageNum === currentPage
                            ? 'btn-pagination btn-pagination-active'
                            : 'btn-pagination'
                        }
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === sortedAndPaginatedStocks.totalPages}
                    className="btn-pagination"
                    title="Next page"
                  >
                    Next →
                  </button>
                  <button
                    onClick={() => handlePageChange(sortedAndPaginatedStocks.totalPages)}
                    disabled={currentPage === sortedAndPaginatedStocks.totalPages}
                    className="btn-pagination"
                    title="Last page"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Wizard Modal Overlay */}
      {showWizardModal && (
        <div className="wizard-modal-overlay">
          <div className="wizard-modal-container">
            <button
              className="wizard-modal-close"
              onClick={() => setShowWizardModal(false)}
              aria-label="Close wizard"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <BeginnerFlowWizard onComplete={handleWizardCompleteFromModal} />
          </div>
        </div>
      )}

      {/* Save Preset Modal */}
      <SavePresetModal
        isOpen={showSavePresetModal}
        onClose={() => setShowSavePresetModal(false)}
        screenerFilters={advancedFilters}
        onPresetSaved={(presetId) => {
          setActivePresetId(presetId);
          setShowSavePresetModal(false);
        }}
      />
    </Layout>
  );
};

export default StockScreener;